import { useState } from "react";
import { sendSlackMessageTracked } from "../../../../models/notifications";
import HorizontalSectionCard from "../../../general/horizontalSectionCard";
import Button from "../../../general/ui-blocks/elements/button";
import NoImageSectionCard from "../../../general/noImageSectionCard";
import Accordion from "../../../general/ui-blocks/elements/accordion";

const GOLD = "#CDAF4B";

function CurriculumRequest() {
    const [email, setEmail] = useState("");
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!email) return;
        sendSlackMessageTracked(`📋 Advanced Curriculum Request — The Residency\nEmail: ${email}`);
        sendSlackMessageTracked(`🚨 Curriculum Request — The Residency\nEmail: ${email}`, "#high-alerts");
        setSubmitted(true);
    };

    return (
        <div className="rounded-lg border p-6 mt-6" style={{ backgroundColor: "#fffdf5", borderColor: GOLD }}>
            <p className="font-semibold mb-1" style={{ fontSize: "clamp(1rem, 2vw, 1.1rem)" }}>
                Request the Advanced Curriculum
            </p>
            <p className="text-gray-500 text-sm mb-4">
                Get the full 48-day week-by-week breakdown sent to your inbox.
            </p>
            {submitted ? (
                <p className="text-sm font-medium" style={{ color: GOLD }}>
                    Got it — we'll send it your way shortly.
                </p>
            ) : (
                <form onSubmit={handleSubmit} className="flex flex-wrap gap-2">
                    <input
                        type="email"
                        required
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="border border-gray-300 rounded-md px-4 py-2 text-sm flex-1 min-w-0 focus:outline-none"
                        style={{ minWidth: "200px" }}
                    />
                    <button
                        type="submit"
                        className="px-5 py-2 rounded-md text-sm font-semibold text-white"
                        style={{ backgroundColor: GOLD }}
                    >
                        Send It
                    </button>
                </form>
            )}
        </div>
    );
}

const REQUIREMENTS = [
    { n: "1", label: "Complete the application", desc: "Fill out the short online form. It's free." },
    { n: "2", label: "Personal essay", desc: "Tell us who you are beyond your grades. This is the most important part." },
    { n: "3", label: "5-minute video", desc: "Record a short video responding to 3 prompts. Just be yourself." },
    { n: "4", label: "Guardian's signature", desc: "A parent or guardian must sign off before enrollment." },
    { n: "5", label: "Tuition or scholarship", desc: "Secure your seat. Need-based aid is available." },
];

const PHASES = [
    {
        phase: "01",
        weeks: "Week 1 – 2",
        title: "Discovery",
        body: "You talk to real people before you write a single line of code or design a single screen. You define the problem, validate your assumptions, and align the team on what you're actually building and why.",
    },
    {
        phase: "02",
        weeks: "Week 3 – 4",
        title: "Build",
        body: "Heads down. Every decision is informed by what you already learned. You build fast, pressure-test constantly, and make the hard calls that separate a shipped product from a stalled idea.",
    },
    {
        phase: "03",
        weeks: "Week 5 – 6",
        title: "Launch",
        body: "Your product goes live. You execute your go-to-market strategy, reach your first real users, and start building the feedback loop that will define the next phase.",
    },
    {
        phase: "04",
        weeks: "Week 7 – 8",
        title: "Grow",
        body: "This is where you start iterating to grow. Once the program ends, you'll have a startup that's ready to keep going.",
    },
];

export default function ResidencyBody() {
    return (
        <main role="main">

            {/* ── HERO ─────────────────────────────────────────────────────────── */}
            <div className="bg-gray-200 py-5">
                <div className="container pb-5" style={{ paddingTop: "calc(80px + 2rem)" }}>

                    <div className="row align-items-center g-5">
                        {/* Left: text */}
                        <div className="col-12 col-lg-6">
                            <span
                                className="text-xs font-bold tracking-[0.2em] uppercase px-3 py-1 mb-4 inline-block"
                                style={{ backgroundColor: GOLD, color: "#fff" }}
                            >
                                Pure Gold · 48-Day Intensive
                            </span>

                            <h1 className="fw-bold mb-4" style={{ fontSize: "clamp(2.5rem, 7vw, 5.5rem)", lineHeight: 1.05 }}>
                                Go All the Way
                            </h1>

                            <p className="mb-5" style={{ maxWidth: 520, fontSize: "1.1rem", lineHeight: 1.75, color: "#4b5563" }}>
                                The Residency is the most selective program Extern Academy offers.
                                A team of 10 externs grow the product out into a respectable company.
                                20 seats. May 2027.
                            </p>

                            <div className="d-flex flex-wrap gap-3">
                                <a
                                    href="/apply"
                                    className="btn btn-lg d-inline-flex align-items-center gap-2 font-semibold text-white"
                                    style={{ backgroundColor: GOLD, borderColor: GOLD }}
                                >
                                    Begin Your Application <span className="material-symbols-outlined">arrow_forward</span>
                                </a>
                                <Button text="LEARN ABOUT FINANCIAL AID" ctaURL="/financial-aid" variant="btn-secondary" />
                            </div>

                            <div className="mt-5 d-flex flex-wrap gap-4" style={{ color: "#9ca3af" }}>
                                <span className="text-xs tracking-widest uppercase">Grades 11 & 12</span>
                                <span>·</span>
                                <span className="text-xs tracking-widest uppercase">48 Days</span>
                                <span>·</span>
                                <span className="text-xs tracking-widest uppercase">20 Seats</span>
                                <span>·</span>
                                <span className="text-xs tracking-widest uppercase">May 2027</span>
                            </div>
                        </div>

                        {/* Right: hero image */}
                        <div className="col-12 col-lg-6">
                            <div className="position-relative">
                                <img
                                    src="https://storage.googleapis.com/extern-academy.firebasestorage.app/images/3769021.webp"
                                    alt="Student focused on building"
                                    className="img-fluid w-100"
                                    style={{
                                        objectFit: "cover",
                                        aspectRatio: "4/5",
                                        borderBottom: `6px solid ${GOLD}`,
                                    }}
                                />
                                <div
                                    className="position-absolute bottom-0 start-0 px-4 py-3"
                                    style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
                                >
                                    <p className="text-white mb-0 text-sm font-semibold tracking-widest uppercase" style={{ letterSpacing: "0.15em" }}>
                                        The Residency &nbsp;·&nbsp; May 2027
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── CURRICULUM ───────────────────────────────────────────────────── */}
            <div className="container py-16">
                <NoImageSectionCard
                    title={<strong>48 days. One team.</strong>}
                    blurb={<p className="text-gray-500">The curriculum is structured across four phases. Every phase is intentional. Every week builds on the last.</p>}
                    shouldRemovePatternWrap={true}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
                    {PHASES.map(({ phase, weeks, title, body }) => (
                        <div key={phase} className="border rounded-lg bg-white shadow-sm overflow-hidden">
                            <div className="p-3 text-white text-sm font-semibold" style={{ backgroundColor: GOLD }}>
                                {weeks}
                            </div>
                            <div className="p-5">
                                <div className="d-flex align-items-center gap-3 mb-3">
                                    <span className="fw-bold" style={{ fontSize: "2.5rem", color: "#e5e7eb", lineHeight: 1 }}>{phase}</span>
                                    <h3 className="fw-bold mb-0" style={{ fontSize: "1.3rem" }}>{title}</h3>
                                </div>
                                <p className="text-gray-500 mb-0">{body}</p>
                            </div>
                        </div>
                    ))}
                </div>
                <CurriculumRequest />
            </div>

            {/* ── YOUR ROLE ────────────────────────────────────────────────────── */}
            <div className="bg-gray-200">
                <HorizontalSectionCard
                    title={<h2 className="text-2xl md:text-4xl"><strong>You won't be a participant.<br />You'll hold a title.</strong></h2>}
                    blurb={
                        <div className="space-y-4">
                            <p>
                                Every Residency student is assigned a C-level role on their team.
                                By the end of 48 days, you'll have a genuine
                                command of what that title means: the decisions it demands, the
                                craft it requires, and the weight it carries.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {[
                                    { title: "CEO", sub: "Chief Executive Officer" },
                                    { title: "CTO", sub: "Chief Technology Officer" },
                                    { title: "CMO", sub: "Chief Marketing Officer" },
                                    { title: "CPO", sub: "Chief Product Officer" },
                                ].map(({ title, sub }) => (
                                    <div key={title} className="border rounded-lg p-4 bg-white shadow-sm">
                                        <p className="fw-bold mb-1" style={{ color: GOLD, fontSize: "1.4rem" }}>{title}</p>
                                        <p className="text-gray-500 text-sm mb-0">{sub}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    }
                    imageDirection="left"
                    primaryImageURL="https://storage.googleapis.com/extern-academy.firebasestorage.app/images/3184418.webp"
                    primaryImageALT="Student leading a team"
                    secondaryImageURL="https://storage.googleapis.com/extern-academy.firebasestorage.app/images/1181396.webp"
                    secondaryImageALT="Startup team in a meeting"
                />
            </div>

            {/* ── WHAT YOU CARRY OUT ───────────────────────────────────────────── */}
            <div className="container py-16">
                <NoImageSectionCard
                    title={<strong>What you carry out of the Residency.</strong>}
                    shouldRemovePatternWrap={true}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
                    {[
                        { label: "A Live Startup", body: "Not a portfolio project. A real product with real users. Stays live for a full year after the program ends — and doesn't have to stop there." },
                        { label: "Certificate of Completion", body: "Proof you completed the most demanding program we offer. The most selective cohort. The longest build. The highest bar." },
                        { label: "Letter of Recommendation", body: "Written by your Startup Trainer — a founder who raised capital and watched you work for 48 days. Not a form letter. A real account of what you built and how you led." },
                        { label: "Golden Ticket", body: "Priority access to every future Extern Academy cohort, for life. No re-application. No waitlist. You've already proven you belong here." },
                        { label: "Your First Real Network", body: "Twenty-four students who built with you under real pressure. That's a different kind of relationship than a classroom ever produces." },
                        { label: "Clarity", body: "Forty-eight days of real decisions tells you more about yourself than four years of coursework could. You'll leave knowing exactly what you're capable of — and what to do next." },
                    ].map(({ label, body }) => (
                        <div key={label} className="border rounded-lg p-4 bg-white shadow-sm" style={{ borderLeft: `3px solid ${GOLD}` }}>
                            <h5 className="fw-bold mb-2" style={{ color: GOLD }}>{label}</h5>
                            <p className="text-gray-500 mb-0 text-sm">{body}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── REQUIREMENTS ─────────────────────────────────────────────────── */}
            <div className="bg-gray-200 py-16">
                <div className="container">
                    <div className="mb-8">
                        <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: GOLD }}>Admissions</p>
                        <h2 className="fw-bold" style={{ fontSize: "clamp(1.8rem, 4vw, 2.8rem)" }}>How to apply.</h2>
                        <p className="text-gray-500 mt-2" style={{ maxWidth: 520 }}>
                            The application is free. The process is selective. Only the strongest applicants are admitted — 20 seats, no exceptions.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        {REQUIREMENTS.map(({ n, label, desc }) => (
                            <div key={n} className="border rounded-lg bg-white shadow-sm overflow-hidden flex flex-col">
                                <div className="p-3 text-white text-center fw-bold text-2xl" style={{ backgroundColor: GOLD }}>{n}</div>
                                <div className="p-4 flex-1">
                                    <h6 className="fw-bold mb-2">{label}</h6>
                                    <p className="text-gray-500 text-sm mb-0">{desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── FAQs ─────────────────────────────────────────────────────────── */}
            <div className="container py-16">
                <NoImageSectionCard
                    title={<strong>Common questions.</strong>}
                    shouldRemovePatternWrap={true}
                />
                <Accordion title="Who is eligible for the Residency?" blurb={
                    <div className="space-y-3">
                        <p>Grades 11 and 12. Most applicants are 16 to 18. The Residency doesn't require prior startup experience, it only requires someone who is genuinely serious about building something. You'll know if that's you.</p>
                    </div>
                } />
                <Accordion title="How is the Residency different from the Startup Sprint and Founder Track?" blurb={
                    <div className="space-y-3">
                        <p>The Sprint (14 days) and Founder Track (28 days) end at launch. The Residency (48 days) keeps going. You take a C-level title, own your function, and see the company through from discovery to growth.</p>
                        <p>It's the only program where the goal isn't just a live product. It's a startup with early traction.</p>
                    </div>
                } />
                <Accordion title="What does 'tuition by application' mean?" blurb={
                    <p>Tuition is confirmed as part of the admissions process. We do this to ensure every admitted student has a financial path forward before a seat is offered. Need-based scholarships are available. Indicate your interest in the application and our team will follow up confidentially.</p>
                } />
                <Accordion title="Is financial aid available?" blurb={
                    <p>Yes. Financial circumstances should never be a barrier to opportunity. Aid is awarded on a rolling basis while funds are available — applying early gives you the best chance. Visit the <a href="/financial-aid" style={{ color: GOLD }}>Financial Aid page</a> for details.</p>
                } />
            </div>

            {/* ── FINAL CTA ────────────────────────────────────────────────────── */}
            <div className="bg-gray-200">
                <HorizontalSectionCard
                    title={<div style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)" }}>You know if this is{" "}<strong>you</strong>.</div>}
                    blurb={
                        <p className="text-gray-500">
                            20 seats. May 2027. Applications reviewed on a rolling basis.
                        </p>
                    }
                    imageDirection="right"
                    imageBackgroundGridOff={true}
                    primaryImageURL="https://firebasestorage.googleapis.com/v0/b/extern-academy.firebasestorage.app/o/website%2Fimages%2Feagle.png?alt=media&token=c38d361d-d72b-4e09-bc99-3634e87a2884"
                    primaryImageALT="Extern Academy Eagle Logo"
                    primaryButtonRow={
                        <div className="d-flex flex-wrap gap-3">
                            <a
                                href="/apply"
                                className="btn btn-lg d-inline-flex align-items-center gap-2 font-semibold text-white"
                                style={{ backgroundColor: GOLD, borderColor: GOLD }}
                            >
                                Begin Your Application <span className="material-symbols-outlined">arrow_forward</span>
                            </a>
                            <Button text="CONTACT US" ctaURL="/contact-us" variant="btn-secondary" />
                        </div>
                    }
                />
            </div>

        </main>
    );
}
